import CIMSymbol from '@arcgis/core/symbols/CIMSymbol';
import MapView from '@arcgis/core/views/MapView';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import type { Feature } from '@fulcrumapp/fulcrum-core';
import { MapPlugin } from '../../../shared/map/arcgis-types';
import { buildGraphic } from '../../../sketch-editor/graphics-handler';
import { GraphicStyle } from '../../../sketch-editor/graphic-styles';
import type { Metadata } from '../display/media-displayer';
import { hasFeatureCoordinates } from '../feature-coordinates';
import { defaultExtents } from '../../../types/geo';

const PHOTO_MARKER_LAYER_ID = 'photo-marker-layer';

const hasValidCoords = (lat?: number, lng?: number): boolean => (
  lat != null && lng != null && lat !== 0 && lng !== 0
);

// GPS image direction (gps_img_direction) is degrees clockwise from north.
// CIMPictureMarker rotation is counterclockwise, so we negate the bearing to convert.
const DIRECTION_ARROW = `data:image/svg+xml;base64,${btoa(
  '<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">'
  + '  <path opacity=".2" d="M36.515 40.97C40.338 42.693 43 46.534 43 51c0 6.075-4.925 11-11 11s-11-4.925-11-11c0-4.147 2.296-7.756 5.685-9.63L8 4h47z" fill="#ee3d2d"/>'
  + '  <path fill-rule="evenodd" clip-rule="evenodd" d="M53.658 3.328a1.501 1.501 0 0 1 2.684 1.342L37.838 41.677A10.99 10.99 0 0 1 43 51c0 6.075-4.925 11-11 11s-11-4.925-11-11c0-3.605 1.735-6.807 4.416-8.813L6.658 4.67a1.501 1.501 0 0 1 2.684-1.342l18.703 37.408a11 11 0 0 1 7.055-.291zM31.5 54.354l-5.171-10.342a9 9 0 1 0 10.61-.536zm.5-12.355a9 9 0 0 0-3.057.534l2.557 5.112 2.687-5.376a9 9 0 0 0-2.187-.27" fill="#ee3d2d"/>'
  + '</svg>',
)}`;

const createDirectionArrowSymbol = (angle: number): CIMSymbol => new CIMSymbol({
  data: {
    type: 'CIMSymbolReference',
    symbol: {
      type: 'CIMPointSymbol',
      symbolLayers: [
        {
          type: 'CIMPictureMarker',
          enable: true,
          size: 50,
          rotation: -angle,
          url: DIRECTION_ARROW,
        },
      ],
    },
  },
});

// Returns the focal graphic to navigate to (the photo marker when present, otherwise
// the feature marker). The record marker is always added for context but should not
// influence the map center when a photo location is available.
const populateMarkerLayer = (layer: GraphicsLayer, feature?: Feature, metadata?: Metadata): Graphic | undefined => {
  let focalGraphic: Graphic | undefined;

  if (hasValidCoords(metadata?.latitude, metadata?.longitude)) {
    const parsedDirection = metadata?.exif?.gps_img_direction != null ? +metadata.exif.gps_img_direction : NaN;

    if (Number.isFinite(parsedDirection)) {
      const directionGraphic = new Graphic({
        geometry: new Point({ latitude: metadata!.latitude!, longitude: metadata!.longitude! }),
        symbol: createDirectionArrowSymbol(parsedDirection),
      });
      layer.add(directionGraphic);
      focalGraphic = directionGraphic;
    } else {
      const photoGraphic = buildGraphic(
        { type: 'Point', coordinates: [metadata!.longitude!, metadata!.latitude!] },
        null,
        GraphicStyle.BASIC_GEOMETRY,
      );
      layer.add(photoGraphic);
      focalGraphic = photoGraphic;
    }
  }

  const featureCoords = hasFeatureCoordinates(feature);
  if (featureCoords) {
    const featureGraphic = buildGraphic(
      { type: 'Point', coordinates: [featureCoords.longitude, featureCoords.latitude] },
      null,
      GraphicStyle.PARENT_GEOMETRY,
    );
    layer.add(featureGraphic);
    focalGraphic = focalGraphic ?? featureGraphic;
  }

  return focalGraphic;
};

const PhotoMarkerPlugin = (feature?: Feature, metadata?: Metadata): MapPlugin => {
  // Tracks whether this plugin instance has already applied its markers. Set to true
  // by whichever of extendMap/onChange runs first, preventing spurious re-navigation
  // on unrelated re-renders. A new instance is created each time metadata/feature
  // changes, so a fresh hasUpdated=false is correct for each navigation.
  let hasUpdated = false;

  const applyToLayer = (mapView: MapView, layer: GraphicsLayer): void => {
    const focalGraphic = populateMarkerLayer(layer, feature, metadata);
    if (focalGraphic) {
      mapView.goTo(focalGraphic);
    } else {
      const { latitude, longitude, zoom } = defaultExtents;
      mapView.goTo({ target: new Point({ latitude, longitude }), zoom });
    }
  };

  return {
    extendMap: (mapView: MapView): void => {
      if (!mapView?.map) {
        return;
      }
      hasUpdated = true;
      const graphicsLayer = new GraphicsLayer({ id: PHOTO_MARKER_LAYER_ID });
      mapView.map.add(graphicsLayer);
      applyToLayer(mapView, graphicsLayer);
    },

    onChange: (mapView: MapView): void => {
      if (hasUpdated) {
        return;
      }
      hasUpdated = true;
      const graphicsLayer = mapView.map?.findLayerById(PHOTO_MARKER_LAYER_ID) as GraphicsLayer | undefined;
      if (!graphicsLayer) {
        return;
      }
      graphicsLayer.removeAll();
      applyToLayer(mapView, graphicsLayer);
    },
  };
};

export default PhotoMarkerPlugin;
